import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface Evaluation {
  id?: string;
  ao_id: string;
  nom: string;
  description: string;
  ordre: number;
  ponderation_globale_pct: number;
}

interface EvaluationListProps {
  evaluations: Evaluation[];
  activeEvaluationId: string | null;
  criteresMap: Record<string, any[]>;
  totalPonderation: number;
  onSelectEvaluation: (id: string) => void;
  onAddEvaluation: () => void;
  onDistributeEqually: () => void;
}

export const EvaluationList: React.FC<EvaluationListProps> = ({
  evaluations,
  activeEvaluationId,
  criteresMap,
  totalPonderation,
  onSelectEvaluation,
  onAddEvaluation,
  onDistributeEqually
}) => {
  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 flex-shrink-0">
        <CardTitle className="text-base">Évaluations</CardTitle>
        <div className="flex gap-2 pt-2">
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onDistributeEqually}
            disabled={evaluations.length === 0}
            className="flex-1 text-xs"
          >
            Répartir équitablement les Evaluations
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onAddEvaluation}
            className="flex-1 text-xs"
          >
            + Ajouter une Evaluation
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-auto space-y-2 p-3 min-h-0">
        {evaluations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <p>Aucune évaluation.</p>
            <p className="text-xs">Cliquez sur "Ajouter"</p>
          </div>
        ) : (
          evaluations.map((evaluation, index) => {
            const evalId = evaluation.id || `new-${index}`;
            const isActive = activeEvaluationId === evalId;
            const criteres = criteresMap[evalId] || [];
            
            return (
              <button
                key={evalId}
                type="button"
                onClick={() => onSelectEvaluation(evalId)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  isActive
                    ? 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/15'
                    : 'bg-card hover:bg-accent/50 border-border'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className={`text-sm font-medium line-clamp-1 ${
                    isActive ? 'text-blue-700 dark:text-blue-400' : 'text-foreground'
                  }`}>
                    {evaluation.nom || `Évaluation ${index + 1}`}
                  </span>
                  <Badge variant="secondary" className="text-xs shrink-0">
                    {evaluation.ponderation_globale_pct}%
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {criteres.length} {criteres.length > 1 ? 'critères' : 'critère'}
                  </Badge>
                  
                  {criteres.length > 0 && (() => {
                    const totalCriteres = criteres.reduce((sum, c) => sum + (c.ponderation_pct || 0), 0);
                    const isValid = totalCriteres === 100;
                    
                    return (
                      <Badge 
                        variant={isValid ? "default" : "destructive"} 
                        className={`text-xs ${isValid ? 'bg-green-600 hover:bg-green-700 border-transparent' : ''}`}
                      >
                        {totalCriteres}%
                      </Badge>
                    );
                  })()}
                </div>
              </button>
            );
          })
        )}
      </CardContent>
      
      <div className="border-t px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total pondération:</span>
          <span className={`font-medium ${
            totalPonderation === 100 ? 'text-green-600' : 'text-destructive'
          }`}>
            {totalPonderation}%
          </span>
        </div>
        {totalPonderation !== 100 && (
          <p className="text-xs text-destructive mt-1">
            Doit être égal à 100%
          </p>
        )}
      </div>
    </Card>
  );
};
